import React from 'react';
import PropTypes from 'prop-types';
import { Send, MessageSquare, Check, X, PanelRightClose } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BlueGeckoIcon from '../../../../assets/chatbot.png';

const markdownComponents = {
  h1: ({ node, children, ...props }) => <h1 style={{ fontSize: '1.4em', fontWeight: 'bold', margin: '12px 0 6px', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</h1>,
  h2: ({ node, children, ...props }) => <h2 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '10px 0 6px', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</h2>,
  h3: ({ node, children, ...props }) => <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '8px 0 4px', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</h3>,
  p: ({ node, children, ...props }) => <p style={{ margin: '0 0 8px 0', color: '#000000', lineHeight: '1.5', fontFamily: 'inherit' }} {...props}>{children}</p>,
  ul: ({ node, children, ...props }) => <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0 0 8px 0', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</ul>,
  ol: ({ node, children, ...props }) => <ol style={{ listStyleType: 'decimal', paddingLeft: '20px', margin: '0 0 8px 0', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</ol>,
  li: ({ node, children, ...props }) => <li style={{ margin: '0 0 4px 0', color: '#000000', fontFamily: 'inherit' }} {...props}>{children}</li>,
  code: ({ node, inline, children, ...props }) => <code style={{ backgroundColor: '#f1f5f9', padding: '2px 4px', borderRadius: '4px', fontFamily: 'monospace', color: '#000000' }} {...props}>{children}</code>,
  pre: ({ node, children, ...props }) => <pre style={{ backgroundColor: '#f1f5f9', padding: '8px', borderRadius: '6px', overflowX: 'auto', margin: '8px 0', fontFamily: 'monospace', color: '#000000' }} {...props}>{children}</pre>
};

const AIChat = ({
  scAiInput,
  setScAiInput,
  scAiMessages,
  scAiLoading,
  handleAiAlter,
  scPendingChange,
  handleAcceptChange,
  handleRejectChange,
  scAiPanelOpen,
  setScAiPanelOpen
}) => {
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [scAiMessages, scAiLoading]);

  const suggestions = [
    'Add indexes / constraints',
    'Optimise for performance',
    'Convert to upsert / soft-delete',
  ];

  return (
    <div className="scriptly-ai-sidebar">
      <div className="ai-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="ai-avatar shadow-[0_0_12px_rgba(59,130,246,0.2)]" style={{ background: 'transparent' }}>
            <img src={BlueGeckoIcon} alt="BlueGecko AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-slate-600">BlueGecko AI</div>
            <div className="text-[10px] text-slate-500 font-medium">Ask BlueGecko to Modify and Explian the script</div>
          </div>
        </div>
        <button
          onClick={() => setScAiPanelOpen(false)}
          className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
          title="Close AI Assistant"
        >
          <PanelRightClose size={14} />
        </button>
      </div>

      <div className="ai-chat-messages" ref={scrollRef}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <MessageSquare size={14} className="text-blue-500" />
          </div>
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Assistant</span>
        </div>

        {scAiMessages.length === 0 ? (
          <div className="ai-suggestion-box">
            <div className="text-[12px] font-bold text-blue-400 mb-3">Hi! I can help you:</div>
            <div className="flex flex-col gap-2.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  className="text-left text-[11px] text-slate-400 hover:text-blue-500 hover:underline cursor-pointer bg-transparent border-none p-0 outline-none transition-colors"
                  onClick={() => handleAiAlter(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          scAiMessages.map((msg, i) => (
            <div key={`${msg.role}-${i}`} className={`chat-bubble ${msg.role === 'ai' ? 'ai' : 'user'} ${msg.hasPendingChange && scPendingChange ? 'has-pending' : ''} shadow-sm`}>
              <div className="font-medium mb-1">{msg.text}</div>
              {msg.explanation && (
                <div className="ai-explanation-content mt-2 pt-2 border-t border-slate-200 text-[12px] leading-relaxed">
                  {Array.isArray(msg.explanation) ? (
                    <ul className="ai-explanation-list list-disc pl-4 space-y-1" style={{ color: '#000000', fontFamily: "'Inter', sans-serif" }}>
                      {msg.explanation.map((item, idx) => (
                        <li key={idx} className="ai-explanation-item" style={{ color: '#000000', fontSize: '12px' }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : typeof msg.explanation === 'string' ? (
                    <div
                      className="ai-explanation-markdown"
                      style={{ color: '#000000', fontFamily: "'Inter', sans-serif" }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {msg.explanation}
                      </ReactMarkdown>
                    </div>
                  ) : null}
                </div>
              )}
              {msg.role === 'ai' && msg.hasPendingChange && msg.changed !== false && scPendingChange && scPendingChange.messageId === msg.id && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-[10px] font-bold transition-colors shadow-sm"
                    onClick={handleAcceptChange}
                  >
                    <Check size={12} />
                    Apply Changes
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-[10px] font-bold transition-colors shadow-sm"
                    onClick={handleRejectChange}
                  >
                    <X size={12} />
                    Discard
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {scAiLoading && <div className="chat-bubble ai italic animate-pulse">Analyzing your script and applying improvements...</div>}
      </div>

      <div className="chat-input-area">
        <div className="chat-input-container shadow-inner">
          <textarea
            rows={2}
            value={scAiInput}
            onChange={(e) => setScAiInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAiAlter(scAiInput);
              }
            }}
            placeholder="Describe transformation..."
            className="chat-textarea"
          />
          <Send
            size={14}
            className="chat-send-inline"
            onClick={() => handleAiAlter(scAiInput)}
          />
        </div>

      </div>
    </div>
  );
};

AIChat.propTypes = {
  scAiInput: PropTypes.string,
  setScAiInput: PropTypes.func,
  scAiMessages: PropTypes.arrayOf(PropTypes.shape({
    role: PropTypes.oneOf(['ai', 'user']),
    text: PropTypes.string,
    explanation: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ])
  })),
  scAiLoading: PropTypes.bool,
  handleAiAlter: PropTypes.func,
  scPendingChange: PropTypes.object,
  handleAcceptChange: PropTypes.func,
  handleRejectChange: PropTypes.func,
  scAiPanelOpen: PropTypes.bool,
  setScAiPanelOpen: PropTypes.func
};

export default AIChat;
